import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import api from "../../connector/api";
import { useEncodedNavigate, useDecodedParams } from "../../hooks/useSqid";

// Layout Components
import Sidebar from "../components/LeftSidebar";
import Header from "../components/Header";
import RightSidebar from "../components/RightSidebar";

// Tab Views
import ClassroomTab from "../tabs/ClassroomTabs";
import BehaviorTab from "../tabs/BehaviorTab";
import DressCodeTab from "../tabs/DressCodeTab";
import GradebookTab from "../tabs/GradebookTab";
import ReportCardTab from "../tabs/ReportCardTab";
import CitizenshipTab from "../tabs/CitizenshipTab";

/* Toast Notification Component */
const ToastNotification = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, 3500);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg border text-xs font-semibold max-w-sm pointer-events-auto ${
        toast.type === "error"
          ? "bg-rose-50 border-rose-200 text-rose-800"
          : "bg-emerald-50 border-emerald-200 text-emerald-800"
      }`}
    >
      {toast.type === "error" ? (
        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
      ) : (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
      )}
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        className="p-0.5 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
};

const TeacherClassroom = () => {
  // Decode the encoded route param to ensure numeric subject IDs are passed to API endpoints
  const decodedSubjectId = useDecodedParams("subjectId");
  const navigateWithSqid = useEncodedNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [activeClassroom, setActiveClassroom] = useState(null);
  const [allSubjects, setAllSubjects] = useState([]); // Master sidebar list
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(decodedSubjectId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);

  // Manage Active Tab via URL Query Parameters
  const activeTab = searchParams.get("tab") || "classroom";

  // Global Toast Notifications State
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const [currentTermInfo, setCurrentTermInfo] = useState({
    yearName: "",
    termName: "",
    loading: true,
  });

  // Load User Data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get("accounts/me/");
        if (response.data) {
          const userData = response.data.user || response.data;
          setCurrentUser({
            ...userData,
            permissions: response.data.permissions || userData.permissions || [],
          });
        }
      } catch (err) {
        console.error("Could not load user data:", err);
        addToast("Failed to load user credentials.", "error");
      }
    };
    fetchUserData();
  }, [addToast]);

  // Sync selectedSubjectId whenever the decoded URL parameter updates
  useEffect(() => {
    setSelectedSubjectId(decodedSubjectId || null);
  }, [decodedSubjectId]);

  // Fetch lightweight subject list for the left sidebar
  useEffect(() => {
    const fetchAllSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const token = localStorage.getItem("accessToken");
        const response = await api.get("teacher/subjects/", {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });

        const data = response.data.results || response.data;
        const rawSubjects = Array.isArray(data) ? data : [];

        // Map lightweight responses into sidebar items
        const formattedSubjects = rawSubjects.map((subj) => ({
          id: String(subj.id),
          name: subj.display_name || subj.subject_name || "Unnamed Subject",
          displayName: subj.display_name || subj.subject_name,
          code: subj.subject_code || "",
          classroomName: subj.classroom_name || "",
          roomNumber: subj.room_number || "",
          classId: String(subj.classroom_id),
        }));

        setAllSubjects(formattedSubjects);
      } catch (err) {
        console.error("Failed to load teacher subjects list:", err);
        addToast("Failed to load subject list.", "error");
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchAllSubjects();
  }, [addToast]);

  // Active Subject Object derived from selected ID
  const activeSubjectInfo = useMemo(() => {
    if (!selectedSubjectId) return null;
    return allSubjects.find((s) => String(s.id) === String(selectedSubjectId)) || null;
  }, [allSubjects, selectedSubjectId]);

  // Lazy-load Classroom & Student data when a subject is active
  useEffect(() => {
    if (!selectedSubjectId || !activeSubjectInfo?.classId) {
      setActiveClassroom(null);
      setLoading(false);
      return;
    }

    const fetchClassroomDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("accessToken");

        const response = await api.get(
          `teacher/classrooms/${activeSubjectInfo.classId}/`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }
        );

        setActiveClassroom(response.data || null);
      } catch (err) {
        console.error("Failed to load classroom detail:", err);
        setError("Unable to load classroom details.");
        addToast("Unable to load classroom details.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchClassroomDetail();
  }, [selectedSubjectId, activeSubjectInfo, addToast]);

  // Load Term Info
  useEffect(() => {
    const fetchCurrentTerm = async () => {
      try {
        const response = await api.get("year-terms/current/");
        const data = response.data;
        setCurrentTermInfo({
          yearName: data?.school_year?.name || data?.school_year_detail?.name || "",
          termName: data?.term?.name || data?.term_detail?.name || "",
          loading: false,
        });
      } catch (err) {
        console.error("Failed to fetch active term info:", err);
        setCurrentTermInfo({ yearName: "", termName: "", loading: false });
      }
    };
    fetchCurrentTerm();
  }, []);

  // Alphabetically sorted students list
  const activeStudents = useMemo(() => {
    if (!activeClassroom?.students) return [];

    return [...activeClassroom.students].sort((a, b) => {
      const lastNameA = a.last_name || a.first_name || "";
      const lastNameB = b.last_name || b.first_name || "";

      return lastNameA.localeCompare(lastNameB, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });
  }, [activeClassroom]);

  const handleTabChange = useCallback(
    (tabName) => {
      setSearchParams({ tab: tabName });
    },
    [setSearchParams]
  );

  const handleSelectSubject = (subj) => {
    setSelectedSubjectId(subj.id);
    navigateWithSqid("/teacher/classroom", subj.id, {
      search: `?tab=${activeTab}`,
    });
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const handleSetClasses = useCallback((updated) => {
    setActiveClassroom((prev) =>
      typeof updated === "function" ? updated(prev) : updated
    );
  }, []);

  return (
    <div className="flex flex-col h-screen w-full bg-zinc-100 text-zinc-800 font-dynamic-title text-xs antialiased overflow-hidden select-none">
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <ToastNotification key={toast.id} toast={toast} onClose={removeToast} />
          ))}
        </AnimatePresence>
      </div>

      <Header currentTermInfo={currentTermInfo} />

      <div className="flex flex-1 min-h-0 overflow-hidden relative bg-zinc-100">
        <Sidebar
          isLoading={loadingSubjects}
          teacherSubjects={allSubjects.map((subj) => ({
            id: subj.id,
            name: subj.displayName || subj.name,
            code: subj.code,
            classroomName: subj.classroomName,
            roomNumber: subj.roomNumber,
            path: `/teacher/classroom/${subj.id}`,
            onClick: () => handleSelectSubject(subj),
          }))}
          permissions={currentUser?.permissions || ["can_view_teacher_classroom"]}
          user={currentUser || { first_name: "", last_name: "", email: "" }}
          handleLogout={handleLogout}
        />

        <main className="flex-1 overflow-y-auto flex flex-col min-w-0 bg-zinc-100 text-zinc-800 relative custom-scroll">
          {!selectedSubjectId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <h2 className="text-sm font-semibold text-zinc-800 mb-1">
                No Subject Selected
              </h2>
              <p className="text-xs text-zinc-500 max-w-sm">
                Please select a subject from the left sidebar to view its classroom, students, and active rosters.
              </p>
            </div>
          ) : (
            <>
              {activeTab === "classroom" && (
                <ClassroomTab
                  loading={loading}
                  error={error}
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  setClasses={handleSetClasses}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}

              {activeTab === "behavior" && (
                <BehaviorTab
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}

              {activeTab === "citizenship" && (
                <CitizenshipTab
                  loading={loading}
                  error={error}
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  currentUserId={currentUser?.id}
                  setClasses={handleSetClasses}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}

              {activeTab === "dresscode" && (
                <DressCodeTab
                  loading={loading}
                  error={error}
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  currentUserId={currentUser?.id}
                  setClasses={handleSetClasses}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}

              {activeTab === "gradebook" && (
                <GradebookTab
                  loading={loading}
                  error={error}
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}

              {activeTab === "reportcard" && (
                <ReportCardTab
                  loading={loading}
                  error={error}
                  activeClass={activeClassroom}
                  activeStudents={activeStudents}
                  selectedSubjectId={selectedSubjectId}
                  isRightSidebarOpen={isRightSidebarOpen}
                  setIsRightSidebarOpen={setIsRightSidebarOpen}
                  addToast={addToast}
                />
              )}
            </>
          )}
        </main>

        <RightSidebar
          isRightSidebarOpen={isRightSidebarOpen}
          setIsRightSidebarOpen={setIsRightSidebarOpen}
          activeClass={activeClassroom}
          activeStudents={activeStudents}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
};

export default TeacherClassroom;